import { zodResolver } from '@hookform/resolvers/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack } from '@mui/material'
import { convertLesson } from 'api/lessons'
import { CareArticleType } from 'types'
import FormSelect from 'components/form-elements/FormSelect'
import { minButtonWidth } from 'const'
import { UIModal } from 'features/UI'
import React, { useCallback, useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { object, string, TypeOf } from 'zod'

const formSchema = object({
  type: string().min(1, 'Field is required'),
})

type FormType = TypeOf<typeof formSchema>

function ModalContent({
  lessonId,
  onClose,
  afterSubmit,
}: {
  afterSubmit: () => void
  onClose: () => void
  lessonId: number
}) {
  const navigate = useNavigate()

  const formSettings = useMemo(() => {
    return {
      mode: 'onChange' as const,
      resolver: zodResolver(formSchema),
      defaultValues: {
        type: '',
      },
    }
  }, [])

  const methods = useForm<FormType>(formSettings)
  const { handleSubmit } = methods

  const options = useMemo(() => {
    return [
      { label: 'Action Post', value: CareArticleType.Task },
      { label: 'Guide Post', value: CareArticleType.Care },
    ]
  }, [])

  const convertLessonMutation = useMutation(convertLesson)

  const onSubmitHandler: SubmitHandler<FormType> = useCallback(
    (values: FormType) => {
      convertLessonMutation.mutate(
        { id: lessonId, type: values.type as CareArticleType },
        {
          onSuccess: (response) => {
            navigate(`/admin/lms/timeline-post-edit?id=${response.data.id}`, {
              state: {
                notification: 'Lesson has been successfully converted',
              },
            })
            afterSubmit()
          },
        },
      )
    },
    [afterSubmit, convertLessonMutation, lessonId, navigate],
  )

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
          e.preventDefault()
          e.stopPropagation()
          handleSubmit(onSubmitHandler)(e)
        }}
        width="100%"
        noValidate
      >
        <Stack spacing="16px">
          <div>
            The converted Lesson should be copied with all information exclude quiz to the menu
            Manage Timeline posts
          </div>

          <FormSelect label="Select Timeline Post Type" name="type" fullWidth options={options} />

          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 5 }}>
            <Button variant="outlined" onClick={onClose} sx={minButtonWidth}>
              Close
            </Button>

            <LoadingButton
              loading={convertLessonMutation.isLoading}
              variant="contained"
              type="submit"
              sx={minButtonWidth}
            >
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </Box>
    </FormProvider>
  )
}

export function useLessonConvert() {
  const [lessonId, setLessonId] = useState<number>()

  const handleConvertClick = useCallback((id: number) => {
    setLessonId(id)
  }, [])

  const clearLessonId = useCallback(() => {
    setLessonId(undefined)
  }, [])

  const action = useMemo(
    () => ({
      name: 'Convert',
      onClick: handleConvertClick,
    }),
    [handleConvertClick],
  )

  const ConvertModal = useMemo(() => {
    const isOpen = !!lessonId
    return (
      <UIModal title="Convert Modal" isOpen={isOpen} onClose={clearLessonId}>
        <div>
          {isOpen ? (
            <ModalContent onClose={clearLessonId} lessonId={lessonId} afterSubmit={clearLessonId} />
          ) : (
            <Box />
          )}
        </div>
      </UIModal>
    )
  }, [lessonId, clearLessonId])

  return {
    convertAction: action,
    ConvertModal,
  }
}
